import { StyleProvider } from "@ant-design/cssinjs"
import {
	ConfigProvider,
	type ThemeConfig,
} from "components/ConfigProvider/ConfigProvider"
import { FC, ReactNode } from "react"
import "./index.css"

const antdTheme: ThemeConfig = {
	hashed: false,
	components: {
		Button: {
			defaultBg: "var(--color-white)",
			defaultBorderColor: "var(--color-grey-20)",
			defaultGhostBorderColor: "var(--color-white)",
			defaultGhostColor: "var(--color-white)",
			ghostBg: "transparent",
			primaryColor: "var(--color-white)",
			colorLink: "var(--color-green-60)",
			// global
			colorError: "var(--color-red-60)",
			colorPrimary: "var(--color-green-60)",
		},
		Radio: {
			radioSize: 18,
		},
		Form: {
			labelColor: "var(--color-navy-100)",
			colorError: "var(--color-red-40)",
		},
		Segmented: {
			trackBg: "var(--color-white)",
			trackPadding: "0 0 0 0",
			itemColor: "var(--color-navy-100)",
			itemSelectedBg: "var(--color-green-60)",
			itemSelectedColor: "var(--color-white)",
			borderRadius: 0,
		},
		Pagination: {
			itemSize: 36,
		},
		Menu: {
			itemHeight: 48,
		},
		Alert: {
			colorSuccess: "var(--color-green-60)",
			colorInfo: "var(--color-blue-60)",
			colorWarning: "var(--color-yellow-orange-60)",
			colorError: "var(--color-red-60)",
		},
		Notification: {
			colorSuccess: "var(--color-green-60)",
			colorInfo: "var(--color-blue-60)",
			colorWarning: "var(--color-yellow-orange-60)",
			colorError: "var(--color-red-60)",
		},
		Tag: {
			defaultBg: "var(--color-grey-10)",
			defaultColor: "var(--color-navy-100)",
		},
	},
	token: {
		controlInteractiveSize: 18,
		fontFamily: "var(--font-sans)",

		// Structural tokens (like colorTextBase, fontFamily, controlInteractiveSize) do not undergo palette color-math/derivation. Ant Design passes
		// their string values straight through to the generated CSS variable (e.g. --ant-font-family: var(--font-sans);).
		colorPrimary: "#84c99a", // --color-green-60
		colorSuccess: "#84c99a", // --color-green-60
		colorWarning: "#ffbf66", // --color-yellow-orange-60
		colorError: "#e21717", // --color-red-60
		colorInfo: "#4da6ff", // --color-blue-60
		colorLink: "#84c99a", // --color-green-60

		// Text \\
		colorText: "var(--color-navy-100)",
		colorTextBase: "var(--color-navy-100)",
		colorTextSecondary: "var(--color-navy-80)",
		colorTextTertiary: "var(--color-navy-60)",
		colorTextQuaternary: "var(--color-navy-40)",
		colorTextLightSolid: "var(--color-grey-0)",
		colorTextHeading: "var(--color-navy-100)",
		colorTextLabel: "var(--color-navy-80)",
		colorTextDescription: "var(--color-navy-60)",
		colorTextDisabled: "var(--color-navy-40)",
		colorTextPlaceholder: "var(--color-navy-40)",
		// Icon \\
		colorIcon: "var(--color-navy-60)",
		colorIconHover: "var(--color-navy-100)",
		// Fill \\
		colorFill: "var(--color-grey-40)",
		colorFillSecondary: "var(--color-grey-30)",
		colorFillTertiary: "var(--color-grey-20)",
		colorFillQuaternary: "var(--color-grey-10)",
		colorFillContent: "var(--color-grey-30)",
		colorFillContentHover: "var(--color-grey-40)",
		colorFillAlter: "var(--color-grey-10)",
		// Background \\
		colorBgContainer: "var(--color-grey-0)",
		// colorBgBrand: "var(--color-green-0)",
		colorBgElevated: "var(--color-grey-0)",
		colorBgLayout: "var(--color-grey-0)",
		colorBgMask: "color-mix(in oklab, var(--color-grey-100) 45%, transparent)",
		colorBgSpotlight: "var(--color-grey-80)",
		colorBgContainerDisabled: "var(--color-grey-20)",
		colorBgTextActive: "var(--color-grey-40)",
		colorBgTextHover: "var(--color-grey-30)",
		colorBorderBg: "var(--color-grey-0)",
		// Border \\
		colorBorder: "var(--color-grey-20)",
		// colorBorderBrand: "var(--color-mint-0)",
		colorBorderSecondary: "var(--color-grey-10)",
		colorSplit: "var(--color-grey-10)",
		// Primary \\
		colorPrimaryBg: "var(--color-green-0)",
		colorPrimaryBgHover: "var(--color-green-20)",
		colorPrimaryBorder: "var(--color-green-20)",
		colorPrimaryBorderHover: "var(--color-green-40)",
		colorPrimaryHover: "var(--color-green-40)",
		colorPrimaryActive: "var(--color-green-80)",
		colorPrimaryTextHover: "var(--color-green-40)",
		colorPrimaryText: "var(--color-green-60)",
		colorPrimaryTextActive: "var(--color-green-80)",
		// Success \\
		colorSuccessBg: "var(--color-green-0)",
		colorSuccessBgHover: "var(--color-green-20)",
		colorSuccessBorder: "var(--color-green-20)",
		colorSuccessBorderHover: "var(--color-green-40)",
		colorSuccessHover: "var(--color-green-40)",
		colorSuccessActive: "var(--color-green-80)",
		colorSuccessTextHover: "var(--color-green-40)",
		colorSuccessText: "var(--color-green-60)",
		colorSuccessTextActive: "var(--color-green-80)",
		// Warning \\
		colorWarningBg: "var(--color-yellow-orange-0)",
		colorWarningBgHover: "var(--color-yellow-orange-20)",
		colorWarningBorder: "var(--color-yellow-orange-20)",
		colorWarningBorderHover: "var(--color-yellow-orange-40)",
		colorWarningHover: "var(--color-yellow-orange-40)",
		colorWarningActive: "var(--color-yellow-orange-80)",
		colorWarningTextHover: "var(--color-yellow-orange-40)",
		colorWarningText: "var(--color-yellow-orange-60)",
		colorWarningTextActive: "var(--color-yellow-orange-80)",
		// Info \\
		colorInfoBg: "var(--color-blue-0)",
		colorInfoBgHover: "var(--color-blue-20)",
		colorInfoBorder: "var(--color-blue-20)",
		colorInfoBorderHover: "var(--color-blue-40)",
		colorInfoHover: "var(--color-blue-40)",
		colorInfoActive: "var(--color-blue-80)",
		colorInfoTextHover: "var(--color-blue-40)",
		colorInfoText: "var(--color-blue-60)",
		colorInfoTextActive: "var(--color-blue-80)",
		// Error \\
		colorErrorBg: "var(--color-red-0)",
		colorErrorBgHover: "var(--color-red-20)",
		colorErrorBorder: "var(--color-red-20)",
		colorErrorBorderHover: "var(--color-red-40)",
		colorErrorHover: "var(--color-red-40)",
		colorErrorActive: "var(--color-red-80)",
		colorErrorTextHover: "var(--color-red-40)",
		colorErrorText: "var(--color-red-60)",
		colorErrorTextActive: "var(--color-red-80)",
		// Link \\
		colorLinkActive: "var(--color-green-80)",
		colorLinkHover: "var(--color-green-40)",
		// Control \\
		controlItemBgActive: "var(--color-green-0)",
		controlItemBgActiveDisabled: "var(--color-grey-40)",
		controlItemBgActiveHover: "var(--color-green-20)",
		controlItemBgHover: "var(--color-grey-20)",
		controlOutline: "var(--color-green-0)",

		boxShadow: "var(--shadow-base)",
		boxShadowSecondary: "var(--shadow-secondary)",
		boxShadowTertiary: "var(--shadow-tertiary)",
	},
}

export const StylesProvider: FC<{ children: ReactNode }> = ({ children }) => (
	<StyleProvider layer>
		<ConfigProvider theme={antdTheme}>{children}</ConfigProvider>
	</StyleProvider>
)
